import * as React from "react";
import { Alert, Button, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { gettext } from "i18n";
import { timestampToDatetimeString } from "utils/formatting";
import {
  LocationDetails,
  confirmLocation,
  denyLocation,
  loadLocationDetails,
} from "utils/mfa";

type Outcome = "confirmed" | "denied" | null;

/**
 * The page the email about a login from an unusual place links to: shows where
 * the login came from and asks whether it was the user. The link's secret is in
 * the URL's fragment, so it never reaches server logs.
 */
const ConfirmLocationPage: React.FC = () => {
  const token = window.location.hash.replace(/^#/, "");
  const [details, setDetails] = React.useState<LocationDetails | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [outcome, setOutcome] = React.useState<Outcome>(null);
  const [submitting, setSubmitting] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!token) {
      setError(gettext("This link is not valid!"));
      return;
    }
    loadLocationDetails(token)
      .then(setDetails)
      .catch((e) => setError(gettext((e as Error).message)));
  }, [token]);

  const answer = async (wasMe: boolean) => {
    if (
      !wasMe &&
      !window.confirm(
        gettext(
          "This logs everyone out of your account and removes its password. You will set a new password with Forgot Password. Continue?",
        ),
      )
    ) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (wasMe) {
        await confirmLocation(token);
      } else {
        await denyLocation(token);
      }
      setOutcome(wasMe ? "confirmed" : "denied");
    } catch (e) {
      setError(gettext((e as Error).message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ConfirmLocationPage">
      <h4>{gettext("Was this you?")}</h4>
      {error && <Alert variant="danger">{error}</Alert>}
      {outcome === "confirmed" && (
        <Alert variant="success">
          {gettext(
            "Thank you, the login is confirmed. You can continue on the device you were logging in on.",
          )}
        </Alert>
      )}
      {outcome === "denied" && (
        <Alert variant="warning">
          {gettext(
            "Your account is secured: everyone has been logged out and the password removed. Set a new password with Forgot Password, and please contact support@mittausguru.fi.",
          )}
        </Alert>
      )}
      {details && !outcome && (
        <>
          <p>
            {gettext(
              "Someone logged in to the account %1 from a place it is not usually used from:",
              details.email,
            )}
          </p>
          <Table size="sm">
            <tbody>
              <tr>
                <th>{gettext("Country")}</th>
                <td>{details.country ?? gettext("Unknown")}</td>
              </tr>
              <tr>
                <th>{gettext("IP address")}</th>
                <td>{details.ip_address ?? gettext("Unknown")}</td>
              </tr>
              <tr>
                <th>{gettext("Browser")}</th>
                <td className="text-break">
                  {details.user_agent || gettext("Unknown")}
                </td>
              </tr>
              <tr>
                <th>{gettext("Time")}</th>
                <td>{timestampToDatetimeString(details.created_at)}</td>
              </tr>
            </tbody>
          </Table>
          {details.confirmed ? (
            <Alert variant="success">
              {gettext("This login has already been confirmed.")}
            </Alert>
          ) : (
            <>
              {details.expired && (
                <Alert variant="warning">
                  {gettext(
                    "This link has expired. If this was you, log in again to get a new link. If it was not you, you can still secure your account.",
                  )}
                </Alert>
              )}
              <div className="d-flex flex-wrap gap-2">
                {!details.expired && details.session_active && (
                  <Button disabled={submitting} onClick={() => answer(true)}>
                    {gettext("Yes, it was me")}
                  </Button>
                )}
                <Button
                  variant="outline-danger"
                  disabled={submitting}
                  onClick={() => answer(false)}
                >
                  {gettext("No, it wasn't me")}
                </Button>
              </div>
            </>
          )}
        </>
      )}
      <hr />
      <Link to="/">{gettext("Go to Mittausguru")}</Link>
    </div>
  );
};

export default ConfirmLocationPage;
